import { useEffect, useState } from "react";
import { getAIProfiles } from "../services/aiProfilesService";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { ChevronLeft, ChevronRight, Star, StarBorder, Lock } from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { useSwipeable } from "react-swipeable";

const AIProfilesCarousel = ({ imageSizeMultiplier = 1, onSelectProfile, initialProfileId, showBlocked = true }) => {
  const [profiles, setProfiles] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const { i18n, t } = useTranslation("pitch_start");
  const lang = i18n.language?.startsWith("es") ? "es" : "en";
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [direction, setDirection] = useState(0);

  const imageSize = 96 * imageSizeMultiplier;

  useEffect(() => {
    const preloadImages = (profiles) => {
      profiles.forEach(p => {
        const img = new Image();
        img.src = `/images/ai_profiles/${p.avatar_url}`;
      });
    };

    const fetchProfiles = async () => {
      try {
        const data = await getAIProfiles();
        data.sort((a, b) => a.sortid - b.sortid)
        setProfiles(data);
        preloadImages(data);
        const index = initialProfileId
          ? data.findIndex(p => p.id === initialProfileId)
          : 0;
        const validIndex = index >= 0 ? index : 0;
        setCurrentIndex(validIndex);
        onSelectProfile && onSelectProfile(data[validIndex]);
      } catch (err) {
        console.error("Error loading profiles", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProfiles();
  }, [initialProfileId, onSelectProfile]);

  const handlePrev = () => {
    setDirection(-1);
    const newIndex = currentIndex === 0 ? profiles.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
    onSelectProfile && onSelectProfile(profiles[newIndex]);
  };

  const handleNext = () => {
    setDirection(1);
    const newIndex = currentIndex === profiles.length - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
    onSelectProfile && onSelectProfile(profiles[newIndex]);
  };

  const swipeHandlers = useSwipeable({
    onSwipedLeft: handleNext,
    onSwipedRight: handlePrev,
    preventDefaultTouchmoveEvent: true,
    trackTouch: true,
  });

  const renderDifficultyIcons = (level) => (
    <Box display="flex" alignItems="center" gap={0.5}>
      <Typography
        variant="caption"
        sx={{ fontWeight: 600, color: "#fff" }}
      >
        {t("experience.difficulty")}
      </Typography>
      {[1, 2, 3].map((i) =>
        i <= level ? (
          <Star key={i} fontSize="small" sx={{ color: "#fff" }} />
        ) : (
          <StarBorder key={i} fontSize="small" sx={{ color: "#ccc" }} />
        )
      )}
    </Box>
  );

  const getExperienceLabel = (count) => {
    if (count < 10) return t("experience.newbie");
    if (count < 50) return "+50 " + t("experience.sessions");
    if (count < 200) return "+100 " + t("experience.sessions");
    if (count < 500) return "+200 " + t("experience.sessions");
    if (count < 1000) return "+500 " + t("experience.sessions");
    return "+1000 " + t("experience.sessions");
  };

  if (loading) {
    return (
      <Box textAlign="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (profiles.length === 0) return null;

  const profile = profiles[currentIndex];
  const name = profile[`name_${lang}`];
  const role = profile[`role_${lang}`];
  const description = profile[`description_${lang}`];
  const avatarUrl = `/images/ai_profiles/${profile.avatar_url}`;
  const difficulty = profile.difficulty_level || 1;
  const experience = profile.experience_count || 0;

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      width="100%"
      mx="auto"
      maxWidth={{ sm: 410 }}
      minHeight={{ xs: 380, sm: 380 }}
    >
      <Box
        {...swipeHandlers}
        width="100%"
        maxWidth={isMobile ? "100%" : "600px"}
        position="relative"
        display="flex"
        alignItems="center"
        justifyContent="center"
        px={0}
      >
        <Box
          position="absolute"
          top={{ xs: '185px', sm: '185px' }}
          left={0}
          right={0}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          px={0.5}
          zIndex={10}
        >
          <IconButton
            onClick={handlePrev}
            sx={{
              background: "linear-gradient(to right,rgb(214, 210, 221),rgb(203, 186, 216))",
              color: "#fff",
              width: 30,
              height: 30,
              minWidth: 30,
              minHeight: 30,
              "&:hover": {
                background: "linear-gradient(to right,rgb(147, 129, 182), #7e57c2)"
              }
            }}
          >
            <ChevronLeft />
          </IconButton>
          <IconButton
            onClick={handleNext}
            sx={{
              background: "linear-gradient(to right,rgb(203, 186, 216),rgb(214, 210, 221))",
              color: "#fff",
              width: 30,
              height: 30,
              minWidth: 30,
              minHeight: 30,
              "&:hover": {
                background: "linear-gradient(to right,rgb(147, 129, 182), #7e57c2)"
              }
            }}
          >
            <ChevronRight />
          </IconButton>
        </Box>
        <AnimatePresence initial={false} custom={direction} mode="sync">
          <motion.div
            key={currentIndex}
            custom={direction}
            initial={{ x: direction > 0 ? 300 : -300, opacity: 0, position: "absolute" }}
            animate={{ x: 0, opacity: 1, position: "relative" }}
            exit={{ x: direction > 0 ? -300 : 300, opacity: 0, position: "absolute" }}
            transition={{ type: "spring", stiffness: 120, damping: 20 }}
            style={{ width: "100%" }}
          >
            <Box width="100%" borderRadius={4} overflow="hidden" boxShadow={6} position="relative">
              {showBlocked && profile.blocked && (
                <Lock
                  titleAccess="Not available"
                  sx={{
                    position: "absolute",
                    top: 12,
                    right: 12,
                    zIndex: 12,
                    color: "#ffffff",
                    fontSize: 22,
                  }}
                />
              )}
              {/* <Box sx={{ background: "linear-gradient(to right, #6a1b9a, #7e57c2)", px: 3, py: 1.5 }}> 
              
              linear-gradient(to right, #4a1f68, #6b2fa3)
              linear-gradient(to right, #52207a, #7c3aed)
              linear-gradient(to right, #341547, #4a1f68)
              linear-gradient(to right, #3b1456, #6b21a8)
              linear-gradient(to right, #682d90, #9442cf)
              */}
              <Box sx={{ background: "linear-gradient(to right, #6a1b9a, #7e57c2)", px: 3, py: 1.5 }}>
                {renderDifficultyIcons(difficulty)}
              </Box>

              <Box sx={{ backgroundColor: "#ede7f6", px: 2, py: 2, display: "flex", alignItems: "center", gap: 3 }}>
                <Avatar src={avatarUrl} alt={name} loading="eager" sx={{ width: imageSize, height: imageSize, objectFit: "cover", backgroundColor: "#ddd" }} />
                <Box textAlign="left">
                  <Typography variant="h5" fontWeight={700} sx={{ color: "#000", fontSize: "1.5rem" }}>{name}</Typography>
                  <Typography variant="body1" fontWeight={500} sx={{ color: "#888", mb: 1 }}>{role}</Typography>
                  <Box sx={{
                    display: "inline-block",
                    px: 1.5,
                    py: 0.5,
                    borderRadius: "16px",
                    background:
                      experience < 10
                        ? "linear-gradient(to right, #9575cd, #d1c4e9)"
                        : experience < 50
                        ? "linear-gradient(to right,rgb(231, 174, 18),rgb(228, 205, 155))"
                        : experience < 200
                        ? "linear-gradient(to right, #A5D6A7, #81C784)"
                        : experience < 500
                        ? "linear-gradient(to right,rgb(117, 146, 241), rgb(39, 87, 245))"
                        : experience < 1000
                        ? "linear-gradient(to right, #FFCC80, #FFB74D)"
                        : "linear-gradient(to right, #FFAB91, #FF8A65)"
                        // "linear-gradient(to right, #81D4FA, #4FC3F7)"
                  }}>
                    <Typography variant="caption" fontWeight={600} sx={{ color: "white" }}>{getExperienceLabel(experience)}</Typography>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ backgroundColor: "#f5f3ff", px: 3, py: 3, textAlign: "center", minHeight: { xs: 120, sm: 80 } }}>
                <Typography variant="body1" sx={{ color: "#444", maxWidth: 360, mx: "auto" }}>{description}</Typography>
              </Box>
            </Box>
          </motion.div>
        </AnimatePresence>
      </Box>
    </Box>
  );
};

export default AIProfilesCarousel;
